
import { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { KitSolar, EquipamentoSolar } from "@/types";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Edit, Calculator, Save, RefreshCw } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { v4 as uuidv4 } from "uuid";
import { calcularCustoTelhado } from "./utils/kitSolarCalculations";
import { EquipamentoRapidoForm } from "@/components/solar/EquipamentoRapidoForm";
import { OutroEquipamentoForm } from "./OutroEquipamentoForm";
import { CategorySelect } from "./equipamento/CategorySelect";
import { ModelSelect } from "./equipamento/ModelSelect";
import { QuantityPriceInputs } from "./equipamento/QuantityPriceInputs";

interface KitSolarEditorProps {
  kitInicial?: KitSolar;
  potenciaAlvo?: number;
  onSalvarKit: (kit: KitSolar) => void;
  tipoTelhado?: string;
}

export const KitSolarEditor = ({ kitInicial, potenciaAlvo, onSalvarKit, tipoTelhado }: KitSolarEditorProps) => {
  const [activeTab, setActiveTab] = useState("paineis");
  const [loading, setLoading] = useState(false);
  const [paineis, setPaineis] = useState<EquipamentoSolar[]>([]);
  const [inversores, setInversores] = useState<EquipamentoSolar[]>([]);
  const [estruturas, setEstruturas] = useState<EquipamentoSolar[]>([]);
  const [outrosEquipamentos, setOutrosEquipamentos] = useState<EquipamentoSolar[]>([]);
  const [kitNome, setKitNome] = useState("Kit Solar Personalizado");
  const [kitFornecedor, setKitFornecedor] = useState("Personalizado");
  
  const [marcaPainelSelecionada, setMarcaPainelSelecionada] = useState("");
  const [modeloPainelSelecionado, setModeloPainelSelecionado] = useState("");
  const [quantidadePainel, setQuantidadePainel] = useState(1);
  const [precoPainel, setPrecoPainel] = useState(800);
  
  const [marcaInversorSelecionada, setMarcaInversorSelecionada] = useState("");
  const [modeloInversorSelecionado, setModeloInversorSelecionado] = useState("");
  const [quantidadeInversor, setQuantidadeInversor] = useState(1);
  const [precoInversor, setPrecoInversor] = useState(3500);
  
  const [categoriaOutroSelecionada, setCategoriaOutroSelecionada] = useState("");
  const [itemOutroSelecionado, setItemOutroSelecionado] = useState("");
  const [quantidadeOutro, setQuantidadeOutro] = useState(1);
  const [precoOutro, setPrecoOutro] = useState(500);
  
  const [painelModal, setPainelModal] = useState(false);
  const [inversorModal, setInversorModal] = useState(false);
  const [outroItemModal, setOutroItemModal] = useState(false);
  const [equipamentoEmEdicao, setEquipamentoEmEdicao] = useState<EquipamentoSolar | undefined>(undefined);

  const [savedPaineis, setSavedPaineis] = useState<EquipamentoSolar[]>([]);
  const [savedInversores, setSavedInversores] = useState<EquipamentoSolar[]>([]);
  const [savedOutrosItens, setSavedOutrosItens] = useState<EquipamentoSolar[]>([]);

  // Configurações do catálogo
  const [catalogoPaineis, setCatalogoPaineis] = useState<{ marca: string; modelos: string[] }[]>([]);
  const [catalogoInversores, setCatalogoInversores] = useState<{ marca: string; modelos: string[] }[]>([]);
  const [catalogoOutros, setCatalogoOutros] = useState<{ categoria: string; itens: string[] }[]>([]);

  useEffect(() => {
    // Carregar marcas e modelos personalizados para painéis
    const marcasPaineisSalvas = localStorage.getItem('marcasPaineis');
    const modelosPaineisSalvos = localStorage.getItem('modelosPaineis');
    
    if (marcasPaineisSalvas && modelosPaineisSalvos) {
      const marcas = JSON.parse(marcasPaineisSalvas);
      const modelos = JSON.parse(modelosPaineisSalvos);
      
      const catalogoAtualizado = marcas.map((marca: string) => ({
        marca,
        modelos: modelos[marca] || []
      }));
      
      setCatalogoPaineis(catalogoAtualizado);
    }
    
    // Carregar marcas e modelos personalizados para inversores
    const marcasInversoresSalvas = localStorage.getItem('marcasInversores');
    const modelosInversoresSalvos = localStorage.getItem('modelosInversores');
    
    if (marcasInversoresSalvas && modelosInversoresSalvos) {
      const marcas = JSON.parse(marcasInversoresSalvas);
      const modelos = JSON.parse(modelosInversoresSalvos);
      
      const catalogoAtualizado = marcas.map((marca: string) => ({
        marca,
        modelos: modelos[marca] || []
      }));
      
      setCatalogoInversores(catalogoAtualizado);
    }
    
    // Carregar categorias e itens de outros equipamentos
    const categoriasOutros = localStorage.getItem('categoriasOutrosEquipamentos');
    
    if (categoriasOutros) {
      setCatalogoOutros(JSON.parse(categoriasOutros));
    }
  }, []);

  useEffect(() => {
    const paineisSalvos = localStorage.getItem('paineisCadastrados');
    if (paineisSalvos) {
      setSavedPaineis(JSON.parse(paineisSalvos));
    }

    const inversoresSalvos = localStorage.getItem('inversoresCadastrados');
    if (inversoresSalvos) {
      setSavedInversores(JSON.parse(inversoresSalvos));
    }

    const outrosItensSalvos = localStorage.getItem('outrosEquipamentosCadastrados');
    if (outrosItensSalvos) {
      setSavedOutrosItens(JSON.parse(outrosItensSalvos));
    }
  }, []);

  useEffect(() => {
    if (kitInicial) {
      setPaineis(kitInicial.paineis || []);
      setInversores(kitInicial.inversores || []);
      setEstruturas(kitInicial.estruturas || []);
      setOutrosEquipamentos(kitInicial.outrosEquipamentos || []);
      setKitNome(kitInicial.nome || "Kit Solar Personalizado");
      setKitFornecedor(kitInicial.fornecedor || "Personalizado");
    } else {
      setPaineis([]);
      setInversores([]);
      setEstruturas([]);
      setOutrosEquipamentos([]);
      setKitNome("Kit Solar Personalizado");
      setKitFornecedor("Personalizado");
    }
  }, [kitInicial]);

  const calcularTotais = useCallback(() => {
    const potenciaPaineis = paineis.reduce((total, painel) => {
      return total + (painel.potencia || 0) * painel.quantidade;
    }, 0);

    const precoPaineis = paineis.reduce((total, painel) => {
      return total + painel.precoUnitario * painel.quantidade;
    }, 0);

    const precoInversores = inversores.reduce((total, inversor) => {
      return total + inversor.precoUnitario * inversor.quantidade;
    }, 0);

    const quantidadeTotalPaineis = paineis.reduce((total, painel) => {
      return total + painel.quantidade;
    }, 0);
    const precoEstruturas = quantidadeTotalPaineis * 250;

    const precoOutros = outrosEquipamentos.reduce((total, outro) => {
      return total + outro.precoUnitario * outro.quantidade;
    }, 0);

    const custoTelhado = calcularCustoTelhado(tipoTelhado, potenciaPaineis);

    let precoTotal = precoPaineis + precoInversores + precoEstruturas + precoOutros;
    
    if (custoTelhado > 0) {
      precoTotal += custoTelhado;
    }

    const geracaoEstimada = Math.round(potenciaPaineis * 30 * 24 * 0.2);

    const economiaEstimada = geracaoEstimada * 0.9;
    const paybackEstimado = economiaEstimada > 0 ? Math.round(precoTotal / economiaEstimada) : 0;

    return {
      potenciaSistema: potenciaPaineis,
      precoTotal,
      custoTelhado,
      geracaoEstimada,
      paybackEstimado,
      quantidadeTotalPaineis,
      precoEstruturas
    };
  }, [paineis, inversores, outrosEquipamentos, tipoTelhado]);

  useEffect(() => {
    const { quantidadeTotalPaineis, precoEstruturas } = calcularTotais();
    
    if (estruturas.length === 0 && quantidadeTotalPaineis > 0) {
      setEstruturas([{
        id: uuidv4(),
        tipo: "estrutura",
        marca: "Romagnole",
        modelo: "Estrutura Padrão",
        quantidade: 1,
        precoUnitario: precoEstruturas,
        fornecedor: kitFornecedor,
        disponibilidade: true
      }]);
    } else if (estruturas.length > 0 && quantidadeTotalPaineis > 0) {
      const novasEstruturas = [...estruturas];
      novasEstruturas[0] = {
        ...novasEstruturas[0],
        precoUnitario: precoEstruturas
      };
      setEstruturas(novasEstruturas);
    }
  }, [paineis, kitFornecedor, calcularTotais]);

  const adicionarPainel = () => {
    if (!marcaPainelSelecionada || !modeloPainelSelecionado) {
      toast.error("Selecione a marca e o modelo do painel");
      return;
    }

    // Buscar potência do modelo de painel (se disponível no localStorage)
    let potenciaPainel = 0.4; // Valor padrão
    
    // Tentar encontrar o equipamento nos cadastrados
    const paineisCadastrados = localStorage.getItem('paineisCadastrados');
    if (paineisCadastrados) {
      const paineis = JSON.parse(paineisCadastrados);
      const painelEncontrado = paineis.find((p: EquipamentoSolar) => 
        p.marca === marcaPainelSelecionada && p.modelo === modeloPainelSelecionado
      );
      
      if (painelEncontrado && painelEncontrado.potencia) {
        potenciaPainel = painelEncontrado.potencia;
      }
    }

    const novoPainel: EquipamentoSolar = {
      id: uuidv4(),
      tipo: "painel",
      marca: marcaPainelSelecionada,
      modelo: modeloPainelSelecionado,
      potencia: potenciaPainel,
      quantidade: quantidadePainel,
      precoUnitario: precoPainel,
      fornecedor: kitFornecedor,
      disponibilidade: true,
      eficiencia: 20.5,
      garantia: 25
    };

    setPaineis([...paineis, novoPainel]);
    
    setMarcaPainelSelecionada("");
    setModeloPainelSelecionado("");
    setQuantidadePainel(1);
    setPrecoPainel(800);
    
    toast.success("Painel adicionado com sucesso");
  };

  const adicionarInversor = () => {
    if (!marcaInversorSelecionada || !modeloInversorSelecionado) {
      toast.error("Selecione a marca e o modelo do inversor");
      return;
    }

    // Buscar potência do modelo de inversor (se disponível no localStorage)
    let potenciaInversor = 5; // Valor padrão
    
    // Tentar encontrar o equipamento nos cadastrados
    const inversoresCadastrados = localStorage.getItem('inversoresCadastrados');
    if (inversoresCadastrados) {
      const inversores = JSON.parse(inversoresCadastrados);
      const inversorEncontrado = inversores.find((i: EquipamentoSolar) => 
        i.marca === marcaInversorSelecionada && i.modelo === modeloInversorSelecionado
      );
      
      if (inversorEncontrado && inversorEncontrado.potencia) {
        potenciaInversor = inversorEncontrado.potencia;
      }
    }

    const novoInversor: EquipamentoSolar = {
      id: uuidv4(),
      tipo: "inversor",
      marca: marcaInversorSelecionada,
      modelo: modeloInversorSelecionado,
      potencia: potenciaInversor,
      quantidade: quantidadeInversor,
      precoUnitario: precoInversor,
      fornecedor: kitFornecedor,
      disponibilidade: true,
      garantia: 10
    };

    setInversores([...inversores, novoInversor]);
    
    setMarcaInversorSelecionada("");
    setModeloInversorSelecionado("");
    setQuantidadeInversor(1);
    setPrecoInversor(3500);
    
    toast.success("Inversor adicionado com sucesso");
  };

  const adicionarOutro = () => {
    if (!categoriaOutroSelecionada || !itemOutroSelecionado) {
      toast.error("Selecione a categoria e o item");
      return;
    }

    const novoItem: EquipamentoSolar = {
      id: uuidv4(),
      tipo: "outros",
      marca: categoriaOutroSelecionada,
      modelo: itemOutroSelecionado,
      quantidade: quantidadeOutro,
      precoUnitario: precoOutro,
      fornecedor: kitFornecedor,
      disponibilidade: true
    };

    setOutrosEquipamentos([...outrosEquipamentos, novoItem]);
    
    setCategoriaOutroSelecionada("");
    setItemOutroSelecionado("");
    setQuantidadeOutro(1);
    setPrecoOutro(500);
    
    toast.success("Item adicionado com sucesso");
  };

  const handleSalvarEquipamentoRapido = (equipamento: EquipamentoSolar) => {
    if (equipamento.tipo === "painel") {
      if (equipamentoEmEdicao) {
        setPaineis(paineis.map(p => p.id === equipamento.id ? equipamento : p));
      } else {
        setPaineis([...paineis, equipamento]);
      }
    } else if (equipamento.tipo === "inversor") {
      if (equipamentoEmEdicao) {
        setInversores(inversores.map(i => i.id === equipamento.id ? equipamento : i));
      } else {
        setInversores([...inversores, equipamento]);
      }
    } else if (equipamento.tipo === "outros") {
      if (equipamentoEmEdicao) {
        setOutrosEquipamentos(outrosEquipamentos.map(o => o.id === equipamento.id ? equipamento : o));
      } else {
        setOutrosEquipamentos([...outrosEquipamentos, equipamento]);
      }
    }
    
    setEquipamentoEmEdicao(undefined);
  };

  const abrirModalAdicionarPainel = () => {
    setEquipamentoEmEdicao(undefined);
    setPainelModal(true);
  };

  const abrirModalAdicionarInversor = () => {
    setEquipamentoEmEdicao(undefined);
    setInversorModal(true);
  };

  const abrirModalAdicionarOutroItem = () => {
    setEquipamentoEmEdicao(undefined);
    setOutroItemModal(true);
  };

  const abrirModalEditarEquipamento = (equipamento: EquipamentoSolar) => {
    setEquipamentoEmEdicao(equipamento);
    if (equipamento.tipo === "painel") {
      setPainelModal(true);
    } else if (equipamento.tipo === "inversor") {
      setInversorModal(true);
    } else if (equipamento.tipo === "outros") {
      setOutroItemModal(true);
    }
  };

  const removerEquipamento = (tipo: string, id: string) => {
    if (tipo === "painel") {
      setPaineis(paineis.filter(p => p.id !== id));
    } else if (tipo === "inversor") {
      setInversores(inversores.filter(i => i.id !== id));
    } else if (tipo === "estrutura") {
      setEstruturas(estruturas.filter(e => e.id !== id));
    } else if (tipo === "outros") {
      setOutrosEquipamentos(outrosEquipamentos.filter(o => o.id !== id));
    }
    toast.info("Item removido");
  };

  const usarComponenteSalvo = (componente: EquipamentoSolar) => {
    const novoComponente = {
      ...componente,
      id: uuidv4(),
      quantidade: 1
    };

    if (componente.tipo === 'painel') {
      setPaineis([...paineis, novoComponente]);
      toast.success("Painel adicionado do catálogo");
    } else if (componente.tipo === 'inversor') {
      setInversores([...inversores, novoComponente]);
      toast.success("Inversor adicionado do catálogo");
    } else if (componente.tipo === 'outros') {
      setOutrosEquipamentos([...outrosEquipamentos, novoComponente]);
      toast.success("Item adicionado do catálogo");
    }
  };

  const sugerirConfiguracao = () => {
    if (!potenciaAlvo) {
      toast.error("Potência alvo não definida");
      return;
    }

    setLoading(true);

    setTimeout(() => {
      // Define potenciasPaineis e potenciasInversores para o exemplo
      const potenciasPaineis: any = {
        "Canadian Solar": {
          "CS3N-395MS": 0.395,
          "CS3W-455MS": 0.455
        },
        "JinkoSolar": {
          "Tiger Neo N-type": 0.54,
          "Eagle 72HM": 0.47
        },
        "Trina Solar": {
          "Vertex S": 0.41,
          "Vertex DE19": 0.535
        }
      };
      
      const potenciasInversores: any = {
        "Growatt": {
          "MIN 3000TL-X": 3,
          "MOD 5000TL3-X": 5,
          "MAX 60KTL3 LV": 60
        },
        "Fronius": {
          "Primo 5.0-1": 5,
          "Symo 12.5-3-M": 12.5,
          "Eco 27.0-3-S": 27
        },
        "SMA": {
          "Sunny Boy 3.0": 3,
          "Sunny Tripower 15000TL": 15,
          "Sunny Highpower 100-20": 100
        }
      };
      
      const marcasDisponiveis = Object.keys(potenciasPaineis);
      const marcaAleatoria = marcasDisponiveis[Math.floor(Math.random() * marcasDisponiveis.length)];
      const modelosDisponiveis = Object.keys(potenciasPaineis[marcaAleatoria as keyof typeof potenciasPaineis]);
      const modeloAleatorio = modelosDisponiveis[Math.floor(Math.random() * modelosDisponiveis.length)];
      const potenciaPainel = potenciasPaineis[marcaAleatoria as keyof typeof potenciasPaineis][modeloAleatorio as keyof (typeof potenciasPaineis)[keyof typeof potenciasPaineis]];
      
      const quantidadePaineis = Math.ceil(potenciaAlvo / potenciaPainel);
      
      const novoPainel: EquipamentoSolar = {
        id: uuidv4(),
        tipo: "painel",
        marca: marcaAleatoria,
        modelo: modeloAleatorio,
        potencia: potenciaPainel,
        quantidade: quantidadePaineis,
        precoUnitario: 850,
        fornecedor: "SolarTech",
        disponibilidade: true,
        eficiencia: 20.5,
        garantia: 25
      };
      
      const potenciaInversorNecessaria = potenciaAlvo * 0.85;
      
      const marcasInversores = Object.keys(potenciasInversores);
      const marcaInversorAleatoria = marcasInversores[Math.floor(Math.random() * marcasInversores.length)];
      const modelosInversorDisponiveis = Object.keys(potenciasInversores[marcaInversorAleatoria as keyof typeof potenciasInversores]);
      
      let modeloInversorSelecionado = modelosInversorDisponiveis[0];
      let diferencaMinima = 999;
      
      modelosInversorDisponiveis.forEach(modelo => {
        const potencia = potenciasInversores[marcaInversorAleatoria as keyof typeof potenciasInversores][modelo as keyof (typeof potenciasInversores)[keyof typeof potenciasInversores]];
        const diferenca = Math.abs(potencia - potenciaInversorNecessaria);
        
        if (diferenca < diferencaMinima) {
          diferencaMinima = diferenca;
          modeloInversorSelecionado = modelo;
        }
      });
      
      const potenciaInversor = potenciasInversores[marcaInversorAleatoria as keyof typeof potenciasInversores][modeloInversorSelecionado as keyof (typeof potenciasInversores)[keyof typeof potenciasInversores]];
      
      const novoInversor: EquipamentoSolar = {
        id: uuidv4(),
        tipo: "inversor",
        marca: marcaInversorAleatoria,
        modelo: modeloInversorSelecionado,
        potencia: potenciaInversor,
        quantidade: 1,
        precoUnitario: potenciaInversor * 700,
        fornecedor: "SolarTech",
        disponibilidade: true,
        garantia: 10
      };
      
      const novaEstrutura: EquipamentoSolar = {
        id: uuidv4(),
        tipo: "estrutura",
        marca: "Romagnole",
        modelo: "Estrutura Padrão",
        quantidade: 1,
        precoUnitario: quantidadePaineis * 250,
        fornecedor: "SolarTech",
        disponibilidade: true
      };
      
      const outrosItens: EquipamentoSolar[] = [
        {
          id: uuidv4(),
          tipo: "outros",
          marca: "Cabos",
          modelo: "Solar 6mm (m)",
          quantidade: Math.ceil(quantidadePaineis * 15),
          precoUnitario: 12,
          fornecedor: "SolarTech",
          disponibilidade: true
        },
        {
          id: uuidv4(),
          tipo: "outros",
          marca: "Conectores",
          modelo: "MC4 Par",
          quantidade: Math.ceil(quantidadePaineis * 1.5),
          precoUnitario: 35,
          fornecedor: "SolarTech",
          disponibilidade: true
        },
        {
          id: uuidv4(),
          tipo: "outros",
          marca: "Proteções",
          modelo: "Kit Proteção CC/CA",
          quantidade: 1,
          precoUnitario: 1200,
          fornecedor: "SolarTech",
          disponibilidade: true
        }
      ];
      
      setPaineis([novoPainel]);
      setInversores([novoInversor]);
      setEstruturas([novaEstrutura]);
      setOutrosEquipamentos(outrosItens);
      setKitFornecedor("SolarTech");
      setKitNome(`Kit Otimizado ${potenciaAlvo.toFixed(2)}kWp`);
      
      toast.success("Configuração sugerida com base na potência alvo");
      setLoading(false);
    }, 1500);
  };

  const salvarKit = () => {
    if (paineis.length === 0) {
      toast.error("É necessário adicionar pelo menos um painel");
      return;
    }

    if (inversores.length === 0) {
      toast.error("É necessário adicionar pelo menos um inversor");
      return;
    }

    const totais = calcularTotais();

    const novoKit: KitSolar = {
      id: kitInicial?.id || uuidv4(),
      nome: kitNome,
      fornecedor: kitFornecedor,
      paineis,
      inversores,
      estruturas,
      outrosEquipamentos,
      potenciaSistema: totais.potenciaSistema,
      precoTotal: totais.precoTotal,
      geracaoEstimada: totais.geracaoEstimada,
      custoTelhado: totais.custoTelhado,
      paybackEstimado: totais.paybackEstimado
    };

    onSalvarKit(novoKit);
  };

  const totais = calcularTotais();

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Informações Básicas</CardTitle>
            <CardDescription>Defina o nome e fornecedor do kit</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label htmlFor="kit-nome">Nome do Kit</Label>
                <Input 
                  id="kit-nome" 
                  value={kitNome} 
                  onChange={e => setKitNome(e.target.value)} 
                  placeholder="Ex: Kit Residencial Premium"
                />
              </div>
              <div>
                <Label htmlFor="kit-fornecedor">Fornecedor</Label>
                <Input 
                  id="kit-fornecedor" 
                  value={kitFornecedor} 
                  onChange={e => setKitFornecedor(e.target.value)} 
                  placeholder="Ex: SolarTech"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Resumo do Kit</CardTitle>
            <CardDescription>Visão geral da configuração atual</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Potência do Sistema:</span>
                <span className="font-semibold">{totais.potenciaSistema.toFixed(2)} kWp</span>
              </div>
              <div className="flex justify-between">
                <span>Geração Estimada:</span>
                <span className="font-semibold">{totais.geracaoEstimada} kWh/mês</span>
              </div>
              <div className="flex justify-between">
                <span>Preço Total:</span>
                <span className="font-semibold text-green-600">
                  {totais.precoTotal.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                  })}
                </span>
              </div>
              {totais.custoTelhado > 0 && (
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Inclui custo adicional de telhado:</span>
                  <span>
                    {totais.custoTelhado.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    })}
                  </span>
                </div>
              )}
              {potenciaAlvo && (
                <div className="flex justify-between pt-2 text-sm">
                  <span>Potência alvo recomendada:</span>
                  <span className={`font-semibold ${totais.potenciaSistema >= potenciaAlvo * 0.95 ? 'text-green-600' : 'text-amber-600'}`}>
                    {potenciaAlvo.toFixed(2)} kWp
                  </span>
                </div>
              )}
            </div>
          </CardContent>
          <CardFooter>
            <Button onClick={salvarKit} className="w-full">
              <Save className="mr-2 h-4 w-4" /> Salvar Kit
            </Button>
          </CardFooter>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="paineis">Painéis</TabsTrigger>
          <TabsTrigger value="inversores">Inversores</TabsTrigger>
          <TabsTrigger value="outros">Outros Itens</TabsTrigger>
        </TabsList>
        
        <TabsContent value="paineis">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle>Painéis Solares</CardTitle>
                <CardDescription>Adicione os painéis do seu kit</CardDescription>
              </div>
              <div className="flex space-x-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={abrirModalAdicionarPainel}
                >
                  <Plus className="mr-2 h-4 w-4" /> Novo Painel
                </Button>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={sugerirConfiguracao}
                  disabled={!potenciaAlvo}
                >
                  <Calculator className="mr-2 h-4 w-4" /> Sugerir Config
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {savedPaineis.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium mb-2">Painéis Cadastrados:</h3>
                  <div className="flex flex-wrap gap-2">
                    {savedPaineis.map((painel) => (
                      <Button 
                        key={painel.id} 
                        variant="outline" 
                        size="sm" 
                        onClick={() => usarComponenteSalvo(painel)}
                        className="text-xs"
                      >
                        {painel.marca} {painel.modelo} {painel.potencia}kW
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-6 border p-4 rounded-md space-y-4">
                <h3 className="font-medium">Adicionar Painel</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CategorySelect 
                    categoria={marcaPainelSelecionada} 
                    setCategoria={setMarcaPainelSelecionada} 
                    categorias={catalogoPaineis}
                  />
                  
                  <ModelSelect 
                    categoria={marcaPainelSelecionada} 
                    modelo={modeloPainelSelecionado} 
                    setModelo={setModeloPainelSelecionado} 
                    categorias={catalogoPaineis}
                  />
                </div>
                
                <QuantityPriceInputs 
                  quantidade={quantidadePainel}
                  setQuantidade={setQuantidadePainel}
                  precoUnitario={precoPainel}
                  setPrecoUnitario={setPrecoPainel}
                />
                
                <Button onClick={adicionarPainel} className="w-full">
                  <Plus className="mr-2 h-4 w-4" /> Adicionar Painel
                </Button>
              </div>

              {paineis.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum painel adicionado. Adicione painéis ao seu kit.
                </div>
              ) : (
                <div className="space-y-4">
                  {paineis.map((painel) => (
                    <div key={painel.id} className="flex items-center justify-between border p-3 rounded-md">
                      <div>
                        <div className="font-medium">{painel.marca} {painel.modelo}</div>
                        <div className="text-sm text-muted-foreground">
                          {painel.potencia} kW × {painel.quantidade} un = {(painel.potencia * painel.quantidade).toFixed(2)} kWp
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="text-right mr-4">
                          <div className="font-medium text-green-600">
                            {painel.precoUnitario.toLocaleString('pt-BR', {
                              style: 'currency',
                              currency: 'BRL'
                            })}
                          </div>
                          <div className="text-sm text-muted-foreground">por unidade</div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => abrirModalEditarEquipamento(painel)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removerEquipamento("painel", painel.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="inversores">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle>Inversores</CardTitle>
                <CardDescription>Adicione os inversores do seu kit</CardDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={abrirModalAdicionarInversor}
              >
                <Plus className="mr-2 h-4 w-4" /> Novo Inversor
              </Button>
            </CardHeader>
            <CardContent>
              {savedInversores.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium mb-2">Inversores Cadastrados:</h3>
                  <div className="flex flex-wrap gap-2">
                    {savedInversores.map((inversor) => (
                      <Button 
                        key={inversor.id} 
                        variant="outline" 
                        size="sm" 
                        onClick={() => usarComponenteSalvo(inversor)}
                        className="text-xs"
                      >
                        {inversor.marca} {inversor.modelo} {inversor.potencia}kW
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-6 border p-4 rounded-md space-y-4">
                <h3 className="font-medium">Adicionar Inversor</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CategorySelect 
                    categoria={marcaInversorSelecionada} 
                    setCategoria={setMarcaInversorSelecionada} 
                    categorias={catalogoInversores}
                  />
                  
                  <ModelSelect 
                    categoria={marcaInversorSelecionada} 
                    modelo={modeloInversorSelecionado} 
                    setModelo={setModeloInversorSelecionado} 
                    categorias={catalogoInversores}
                  />
                </div>
                
                <QuantityPriceInputs 
                  quantidade={quantidadeInversor}
                  setQuantidade={setQuantidadeInversor}
                  precoUnitario={precoInversor}
                  setPrecoUnitario={setPrecoInversor}
                />
                
                <Button onClick={adicionarInversor} className="w-full">
                  <Plus className="mr-2 h-4 w-4" /> Adicionar Inversor
                </Button>
              </div>

              {inversores.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum inversor adicionado. Adicione inversores ao seu kit.
                </div>
              ) : (
                <div className="space-y-4">
                  {inversores.map((inversor) => (
                    <div key={inversor.id} className="flex items-center justify-between border p-3 rounded-md">
                      <div>
                        <div className="font-medium">{inversor.marca} {inversor.modelo}</div>
                        <div className="text-sm text-muted-foreground">
                          {inversor.potencia} kW × {inversor.quantidade} un
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="text-right mr-4">
                          <div className="font-medium text-green-600">
                            {inversor.precoUnitario.toLocaleString('pt-BR', {
                              style: 'currency',
                              currency: 'BRL'
                            })}
                          </div>
                          <div className="text-sm text-muted-foreground">por unidade</div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => abrirModalEditarEquipamento(inversor)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removerEquipamento("inversor", inversor.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="outros">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle>Outros Equipamentos</CardTitle>
                <CardDescription>Adicione outros equipamentos ao seu kit</CardDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={abrirModalAdicionarOutroItem}
              >
                <Plus className="mr-2 h-4 w-4" /> Novo Item
              </Button>
            </CardHeader>
            <CardContent>
              {savedOutrosItens.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium mb-2">Itens Cadastrados:</h3>
                  <div className="flex flex-wrap gap-2">
                    {savedOutrosItens.map((item) => (
                      <Button 
                        key={item.id} 
                        variant="outline" 
                        size="sm" 
                        onClick={() => usarComponenteSalvo(item)}
                        className="text-xs"
                      >
                        {item.marca} {item.modelo}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-6 border p-4 rounded-md space-y-4">
                <h3 className="font-medium">Adicionar Outro Item</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CategorySelect 
                    categoria={categoriaOutroSelecionada} 
                    setCategoria={setCategoriaOutroSelecionada} 
                    categorias={catalogoOutros.map(cat => ({ marca: cat.categoria, modelos: cat.itens }))}
                  />
                  
                  <ModelSelect 
                    categoria={categoriaOutroSelecionada} 
                    modelo={itemOutroSelecionado} 
                    setModelo={setItemOutroSelecionado} 
                    categorias={catalogoOutros.map(cat => ({ marca: cat.categoria, modelos: cat.itens }))}
                  />
                </div>
                
                <QuantityPriceInputs 
                  quantidade={quantidadeOutro}
                  setQuantidade={setQuantidadeOutro}
                  precoUnitario={precoOutro}
                  setPrecoUnitario={setPrecoOutro}
                />
                
                <Button onClick={adicionarOutro} className="w-full">
                  <Plus className="mr-2 h-4 w-4" /> Adicionar Item
                </Button>
              </div>

              {estruturas.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium mb-2">Estruturas:</h3>
                  <div className="space-y-4">
                    {estruturas.map((estrutura) => (
                      <div key={estrutura.id} className="flex items-center justify-between border p-3 rounded-md bg-muted/30">
                        <div>
                          <div className="font-medium">{estrutura.marca} {estrutura.modelo}</div>
                          <div className="text-sm text-muted-foreground">
                            Quantidade: {estrutura.quantidade} un
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="text-right mr-4">
                            <div className="font-medium text-green-600">
                              {estrutura.precoUnitario.toLocaleString('pt-BR', {
                                style: 'currency',
                                currency: 'BRL'
                              })}
                            </div>
                            <div className="text-sm text-muted-foreground">total</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {outrosEquipamentos.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum outro equipamento adicionado. Adicione outros equipamentos ao seu kit.
                </div>
              ) : (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium mb-2">Outros Equipamentos:</h3>
                  {outrosEquipamentos.map((outro) => (
                    <div key={outro.id} className="flex items-center justify-between border p-3 rounded-md">
                      <div>
                        <div className="font-medium">{outro.marca} {outro.modelo}</div>
                        <div className="text-sm text-muted-foreground">
                          Quantidade: {outro.quantidade} un
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="text-right mr-4">
                          <div className="font-medium text-green-600">
                            {outro.precoUnitario.toLocaleString('pt-BR', {
                              style: 'currency',
                              currency: 'BRL'
                            })}
                          </div>
                          <div className="text-sm text-muted-foreground">por unidade</div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => abrirModalEditarEquipamento(outro)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removerEquipamento("outros", outro.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
